import {
  ConnectionCheck,
  ConnectionQuality,
  Room,
  RoomEvent,
  Track,
} from "livekit-client";
import {getFunctions, httpsCallable} from "firebase/functions";
import {disconnectCallMedia} from "./callTermination";

import {showSnackbar} from "./Snackbar";
import {
  sendPasswordResetEmail,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
} from "firebase/auth";
import {auth, setLiveKitToken} from "./firebase";
import {requestWakeLock, releaseWakeLock} from "./wakeLock";
import {
  callAudioPublishDefaults,
  readAgentCallAudioBitrateKbps,
  saveAgentCallAudioBitrateKbps,
} from "./callAudioQuality";

const CALL_VOLUME_STORAGE_KEY = "frecall-agent-call-volume";
const readCallVolume = () => {
  const raw = localStorage.getItem(CALL_VOLUME_STORAGE_KEY);
  if (raw === null || raw === "") return 1;
  const stored = Number(raw);
  if (!Number.isFinite(stored)) return 1;
  return Math.min(1, Math.max(0, stored));
};

const controls = {
  volume: 5,
  callVolume: readCallVolume(),
  callAudioBitrateKbps: readAgentCallAudioBitrateKbps(),
  micOn: false,
  cameraOn: false,
  callAnswered: false,
  callerIdOnCall: "",
  agentUserId: "",
  uidOfAgentOnCall: "",
  uidOfCustomerOnCall: "",
  callDuration: 0,
  myInterval: null,
  room: null,
  microphoneTrack: null,
  microphonePermission: "unknown",
  microphoneDevices: [],
  preferredInputDeviceId: localStorage.getItem("frecall-agent-input-device-id") || "",
  preferredInputLabel: localStorage.getItem("frecall-agent-input-device-label") || "System microphone",
  audioBlocked: false,
  playbackDevices: [],
  preferredOutputDeviceId: localStorage.getItem("frecall-agent-output-device-id") || "",
  preferredOutputLabel: localStorage.getItem("frecall-agent-output-device-label") || "System output",
  livekitUrl: "",
  roomId: "",
  channelName: "",
  mediaRoomName: "",
  token: "",
  audioTrackOfCustomer: null,
  audioTrackOfAgent: null,
  callerParticipant: null,
  customerAudioElement: null,
  callerMuted: false,
  callerConnected: false,
  callerActive: false,
  callerSpeaking: false,
  callerAudioStreamState: "inactive",
  callerConnectionQuality: ConnectionQuality.Unknown,
  agentConnectionQuality: ConnectionQuality.Unknown,
  agentSpeaking: false,
  roomConnectionState: "disconnected",
  callPhase: "idle",
  callError: "",
  agentActivityState: "offline",
  intentionalDisconnect: false,
  rooms: [],
  queueSchemaVersion: 1,
  setView: () => {},
  setLoading: () => {},
  setMicOn: () => {},
  setTime: () => {},
  requestWakeLock,
  releaseWakeLock,
  egressId: "",
  recordingStatus: "idle",
  recordingError: "",
  timeAnswered: 0,
  historyDocId: "",
  recordingStartPromise: null,
  endCallPromise: null,
  answerCallPromise: null,
  callAttempt: 0,
  lastHealthEvents: {},
};

const reportHealthEvent = (eventType, participant = "agent") => {
  if (!controls.historyDocId || !controls.roomId) return;
  const key = `${participant}:${eventType}`;
  const now = Date.now();
  if (now - Number(controls.lastHealthEvents[key] || 0) < 15000) return;
  controls.lastHealthEvents[key] = now;
  const report = httpsCallable(getFunctions(), "reportCallHealthEvent");
  report({
    roomId: controls.roomId,
    historyDocId: controls.historyDocId,
    eventType,
    participant,
  }).catch(() => {});
};

const setAgentCallVolume = (value) => {
  const nextVolume = Math.min(1, Math.max(0, Number(value) || 0));
  controls.callVolume = nextVolume;
  localStorage.setItem(CALL_VOLUME_STORAGE_KEY, String(nextVolume));
  if (controls.customerAudioElement) {
    controls.customerAudioElement.volume = nextVolume;
  }
  return nextVolume;
};

const setAgentCallAudioBitrateKbps = (value) => {
  const bitrate = saveAgentCallAudioBitrateKbps(value);
  controls.callAudioBitrateKbps = bitrate;
  return bitrate;
};

const runAgentConnectionDiagnostic = async () => {
  if (controls.callAnswered || controls.callerIdOnCall) {
    return {ok: false, message: "End the current call before testing."};
  }
  const createToken = httpsCallable(getFunctions(), "createLiveKitToken");
  const result = await createToken({
    diagnostic: true,
    roomAdmin: true,
    roomId: controls.roomId,
  });
  const data = result.data || {};
  if (!data.token || !data.url) {
    return {ok: false, message: "Diagnostic token was unavailable."};
  }
  const check = new ConnectionCheck(data.url, data.token, {
    errorsAsWarnings: true,
  });
  const results = [];
  results.push(await check.checkWebsocket());
  results.push(await check.checkWebRTC());
  results.push(await check.checkTURN());
  results.push(await check.checkPublishAudio());
  return {
    ok: check.isSuccess(),
    results: results.map((item) => ({
      name: item.name,
      status: item.status,
      description: item.description,
    })),
  };
};

const startAuthoritativeTimer = (timeAnswered) => {
  const answeredSeconds = Number(timeAnswered || 0);
  if (!Number.isFinite(answeredSeconds) || answeredSeconds <= 0) return;
  controls.timeAnswered = answeredSeconds;
  clearInterval(controls.myInterval);
  const update = () => {
    const elapsed = Math.max(
        0,
        Math.floor(Date.now() / 1000) - controls.timeAnswered,
    );
    controls.callDuration = elapsed;
    controls.setTime(elapsed);
  };
  update();
  controls.myInterval = setInterval(update, 1000);
};

const startRecordingForCurrentCall = async () => {
  if (!controls.callAnswered || !controls.historyDocId ||
      !controls.callerIdOnCall || !controls.callerActive) {
    return null;
  }
  if (controls.recordingStartPromise) return controls.recordingStartPromise;
  if (controls.recordingStatus === "recording" && controls.timeAnswered) {
    startAuthoritativeTimer(controls.timeAnswered);
    return null;
  }

  const functions = getFunctions();
  const startRecording = httpsCallable(functions, "startRecording");
  const room = controls.room;
  const historyDocId = controls.historyDocId;
  const stillCurrent = () => controls.callAnswered &&
    !controls.intentionalDisconnect && controls.room === room &&
    controls.historyDocId === historyDocId;
  controls.recordingStatus = "starting";
  controls.recordingError = "";
  controls.recordingStartPromise = startRecording({
    roomId: controls.roomId,
    roomName: controls.mediaRoomName || controls.channelName,
    historyDocId: controls.historyDocId,
    callerId: controls.callerIdOnCall,
  }).then((recordingResult) => {
    if (!stillCurrent()) return null;
    const recordingData = recordingResult.data || {};
    controls.egressId = recordingData.egressId || controls.egressId || "";
    // The authoritative call timer starts only after the backend verifies both
    // microphone recorders.
    startAuthoritativeTimer(recordingData.timeAnswered);
    if (!recordingData.ok) {
      controls.recordingStatus = "problem";
      controls.recordingError = recordingData.error || "";
      showSnackbar(
          "Recording could not start, so the call was stopped safely.",
          "error",
      );
      controls.intentionalDisconnect = true;
      controls.room?.disconnect?.();
      controls.intentionalDisconnect = false;
      controls.callAnswered = false;
      controls.callPhase = "failed";
      return recordingData;
    }
    controls.recordingStatus = "recording";
    controls.microphoneTrack?.unmute?.();
    controls.callPhase = "active";
    showSnackbar(recordingData.reused ?
      "Recording and timing resumed." : "Call connected and recording started.");
    return recordingData;
  }).catch((error) => {
    if (!stillCurrent()) return null;
    controls.recordingStatus = "problem";
    controls.recordingError = error?.message || "";
    showSnackbar(
        "Recording could not start, so the call was stopped safely.",
        "error",
    );
    controls.intentionalDisconnect = true;
    controls.room?.disconnect?.();
    controls.intentionalDisconnect = false;
    controls.callAnswered = false;
    controls.callPhase = "failed";
    return null;
  }).finally(() => {
    if (controls.room === room && controls.historyDocId === historyDocId) {
      controls.recordingStartPromise = null;
    }
  });
  return controls.recordingStartPromise;
};

const microphoneIsPublishing = () => {
  const publication = controls.room?.localParticipant
      ?.getTrackPublication(Track.Source.Microphone);
  const track = publication?.audioTrack;
  const mediaTrack = track?.mediaStreamTrack;
  const publishing = Boolean(
      publication &&
      track &&
      !publication.isMuted &&
      mediaTrack?.enabled &&
      mediaTrack?.readyState === "live",
  );
  controls.microphoneTrack = publishing ? track : null;
  return publishing;
};

const waitForPublishedMicrophone = async (timeoutMs = 2500) => {
  const startedAt = Date.now();
  while (Date.now() - startedAt < timeoutMs) {
    if (microphoneIsPublishing()) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  return microphoneIsPublishing();
};

const syncPlaybackDevices = async () => {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    controls.playbackDevices = devices
        .filter((device) => device.kind === "audiooutput");
    controls.microphoneDevices = devices
        .filter((device) => device.kind === "audioinput");
  } catch (_) {
    controls.playbackDevices = [];
  }
  return controls.playbackDevices;
};

const agentMicrophoneConstraints = () => ({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  ...(controls.preferredInputDeviceId ? {
    deviceId: {exact: controls.preferredInputDeviceId},
  } : {}),
});

const liveKitMicrophoneOptions = () => ({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  ...(controls.preferredInputDeviceId ? {
    deviceId: {exact: controls.preferredInputDeviceId},
  } : {}),
});

const acquireAgentMicrophoneStream = async () => {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: agentMicrophoneConstraints(),
      video: false,
    });
  } catch (error) {
    if (!controls.preferredInputDeviceId) throw error;

    // Stored device IDs can become stale after a headset or browser-profile
    // change. Fall back to the system input so tests and calls stay aligned.
    controls.preferredInputDeviceId = "";
    controls.preferredInputLabel = "System microphone";
    localStorage.removeItem("frecall-agent-input-device-id");
    localStorage.removeItem("frecall-agent-input-device-label");
    return navigator.mediaDevices.getUserMedia({
      audio: agentMicrophoneConstraints(),
      video: false,
    });
  }
};

const prepareAgentMicrophone = async () => {
  if (!navigator.mediaDevices?.getUserMedia) {
    controls.microphonePermission = "unsupported";
    controls.micOn = false;
    return false;
  }

  controls.microphonePermission = "checking";
  try {
    const stream = await acquireAgentMicrophoneStream();
    stream.getTracks().forEach((track) => track.stop());
    controls.microphonePermission = "ready";
    controls.micOn = true;
    await syncPlaybackDevices();
    return true;
  } catch (_) {
    controls.microphonePermission = "denied";
    controls.micOn = false;
    return false;
  }
};

const switchAgentInput = async (device) => {
  if (!device?.deviceId) return false;
  try {
    controls.preferredInputDeviceId = device.deviceId;
    controls.preferredInputLabel = (device.label || "System microphone").trim();
    localStorage.setItem("frecall-agent-input-device-id", device.deviceId);
    localStorage.setItem("frecall-agent-input-device-label", controls.preferredInputLabel);
    if (controls.callAnswered && controls.micOn) {
      if (!await publishAgentMicrophone()) {
        throw new Error("The selected microphone did not publish.");
      }
    } else if (controls.room?.switchActiveDevice) {
      await controls.room.switchActiveDevice("audioinput", device.deviceId);
    }
    controls.microphonePermission = "ready";
    return true;
  } catch (_) {
    controls.microphonePermission = "unavailable";
    return false;
  }
};

const publishAgentMicrophone = async () => {
  // A disabled microphone is not a successful publication. Returning true
  // here previously let a muted previous call make the next call appear
  // healthy even though no audio track had been published.
  if (!controls.micOn) return false;
  const participant = controls.room?.localParticipant;
  if (!participant) return false;

  try {
    // Always recreate the publication from the selected input. A browser can
    // leave an old WebRTC track in a technically "live" state even though a
    // newly tested microphone is the device producing audible samples.
    await participant.setMicrophoneEnabled(false);
    controls.microphoneTrack = null;
    await participant.setMicrophoneEnabled(true, liveKitMicrophoneOptions());
    if (await waitForPublishedMicrophone()) {
      controls.microphonePermission = "ready";
      return true;
    }

    // A permission grant does not guarantee that LiveKit published a usable
    // track. Recreate it once before asking the agent to choose another input.
    await participant.setMicrophoneEnabled(false);
    controls.microphoneTrack = null;
    await participant.setMicrophoneEnabled(true, liveKitMicrophoneOptions());
    if (await waitForPublishedMicrophone()) {
      controls.microphonePermission = "ready";
      return true;
    }
  } catch (_) {}

  controls.microphoneTrack = null;
  controls.microphonePermission = "unavailable";
  reportHealthEvent("microphone_unavailable", "agent");
  return false;
};

const switchAgentOutput = async (device) => {
  if (!device?.deviceId) return false;
  try {
    if (controls.room?.switchActiveDevice) {
      await controls.room.switchActiveDevice("audiooutput", device.deviceId);
    } else if (controls.customerAudioElement?.setSinkId) {
      await controls.customerAudioElement.setSinkId(device.deviceId);
    } else {
      return false;
    }
    controls.preferredOutputDeviceId = device.deviceId;
    controls.preferredOutputLabel = (device.label || "System output").trim();
    localStorage.setItem("frecall-agent-output-device-id", device.deviceId);
    localStorage.setItem("frecall-agent-output-device-label", controls.preferredOutputLabel);
    return true;
  } catch (_) {
    return false;
  }
};

const resumeAgentAudio = async () => {
  try {
    await controls.room?.startAudio?.();
    await controls.customerAudioElement?.play?.();
    controls.audioBlocked = false;
    return true;
  } catch (_) {
    controls.audioBlocked = true;
    return false;
  }
};

const unlockAgentAudioPlayback = async () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const context = new AudioCtx();
      await context.resume();
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      gain.gain.value = 0;
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start();
      oscillator.stop(context.currentTime + 0.02);
      setTimeout(() => context.close().catch(() => {}), 100);
    }

    const player = document.getElementById("myPlayer");
    if (player) {
      const wasMuted = player.muted;
      player.muted = true;
      await player.play();
      player.pause();
      player.muted = wasMuted;
    }
    controls.audioBlocked = false;
    return true;
  } catch (_) {
    return false;
  }
};

const clearCustomerAudioTrack = () => {
  try {
    controls.customerAudioElement?.remove?.();
  } catch (_) {}
  controls.customerAudioElement = null;
  controls.audioTrackOfCustomer = null;
  controls.callerAudioStreamState = "inactive";
  controls.callerSpeaking = false;
};

const clearCustomerAudio = () => {
  clearCustomerAudioTrack();
  controls.callerParticipant = null;
  controls.callerConnected = false;
  controls.callerActive = false;
  controls.callerMuted = true;
  controls.callerSpeaking = false;
  controls.callerConnectionQuality = ConnectionQuality.Unknown;
};

const isCurrentCustomer = (participant) => Boolean(
    participant &&
    controls.callAnswered && !controls.intentionalDisconnect &&
    !participant.isLocal &&
    participant.identity === controls.uidOfCustomerOnCall,
);

const attachCustomerAudio = async (track, participant) => {
  clearCustomerAudioTrack();
  controls.uidOfCustomerOnCall = participant.identity;
  controls.audioTrackOfCustomer = track;
  controls.callerParticipant = participant;
  controls.callerConnected = true;
  controls.callerActive = true;
  controls.callerMuted = !participant.isMicrophoneEnabled;
  controls.callerSpeaking = Boolean(participant.isSpeaking);
  controls.callerAudioStreamState = "active";
  controls.callerConnectionQuality = participant.connectionQuality;

  const element = track.attach();
  element.autoplay = true;
  element.volume = controls.callVolume;
  element.style.display = "none";
  document.body.appendChild(element);
  controls.customerAudioElement = element;
  try {
    if (controls.preferredOutputDeviceId && element.setSinkId) {
      await element.setSinkId(controls.preferredOutputDeviceId);
    }
    await element.play();
    controls.audioBlocked = false;
  } catch (_) {
    controls.audioBlocked = true;
    reportHealthEvent("audio_blocked", "agent");
  }
};

const initLiveKitRoom = () => {
  controls.room?.removeAllListeners?.();
  controls.room = new Room({
    adaptiveStream: true,
    dynacast: true,
    publishDefaults: callAudioPublishDefaults(
        controls.callAudioBitrateKbps,
    ),
  });
  const room = controls.room;

  controls.room.on(RoomEvent.ConnectionStateChanged, (state) => {
    controls.roomConnectionState = state;
    if (controls.intentionalDisconnect || !controls.callAnswered) return;
    const normalizedState = (state || "").toString().toLowerCase();
    if (normalizedState === "reconnecting") {
      controls.callPhase = "reconnecting";
      reportHealthEvent("reconnecting", "agent");
    } else if (normalizedState === "connected" && controls.callAnswered) {
      if (controls.callPhase === "reconnecting") {
        reportHealthEvent("reconnected", "agent");
        showSnackbar("Connection restored.");
      }
      controls.callPhase = controls.recordingStatus === "recording" ?
        (controls.audioTrackOfCustomer ? "active" : "waiting-caller") :
        "preparing-recording";
      controls.callError = "";
    } else if (
      normalizedState === "disconnected" &&
      controls.callAnswered &&
      !controls.intentionalDisconnect
    ) {
      controls.callPhase = "failed";
      controls.callError = "Connection lost. Check your network and retry.";
    }
  });

  controls.room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
    if (participant?.isLocal) {
      controls.agentConnectionQuality = quality;
      if (quality === ConnectionQuality.Poor) {
        reportHealthEvent("network_poor", "agent");
      } else if (quality === ConnectionQuality.Lost) {
        reportHealthEvent("network_lost", "agent");
      } else if (quality === ConnectionQuality.Good ||
          quality === ConnectionQuality.Excellent) {
        reportHealthEvent("network_recovered", "agent");
      }
      return;
    }

    if (isCurrentCustomer(participant)) {
      controls.callerConnectionQuality = quality;
      if (quality === ConnectionQuality.Poor) {
        reportHealthEvent("network_poor", "caller");
      } else if (quality === ConnectionQuality.Lost) {
        reportHealthEvent("network_lost", "caller");
      } else if (quality === ConnectionQuality.Good ||
          quality === ConnectionQuality.Excellent) {
        reportHealthEvent("network_recovered", "caller");
      }
    }
  });

  controls.room.on(RoomEvent.TrackSubscribed, async (track, _, participant) => {
    if (controls.room === room && track.kind === Track.Kind.Audio &&
        isCurrentCustomer(participant)) {
      await attachCustomerAudio(track, participant);
      if (controls.room !== room || !isCurrentCustomer(participant)) return;
      await startRecordingForCurrentCall();
      if (controls.room !== room || !isCurrentCustomer(participant)) return;
      controls.callPhase = "active";
      controls.callError = "";
      showSnackbar(`Customer connected: ${participant.identity}`);
    }
  });

  controls.room.on(RoomEvent.TrackUnsubscribed, (track, _, participant) => {
    if (track.kind === Track.Kind.Audio && isCurrentCustomer(participant)) {
      clearCustomerAudioTrack();
      controls.callPhase = "waiting-caller";
      showSnackbar("Customer audio paused. Waiting for it to resume.", "warning");
    }
  });

  controls.room.on(RoomEvent.ParticipantConnected, (participant) => {
    if (isCurrentCustomer(participant)) {
      controls.callerConnected = true;
      controls.callerParticipant = participant;
      controls.callerMuted = !participant.isMicrophoneEnabled;
      controls.callerConnectionQuality = participant.connectionQuality;
    }
  });

  controls.room.on(RoomEvent.ParticipantActive, (participant) => {
    if (isCurrentCustomer(participant)) {
      controls.callerActive = true;
      controls.callerParticipant = participant;
      controls.callPhase = controls.audioTrackOfCustomer ? "active" : "waiting-caller";
      showSnackbar("Customer media is ready.");
    }
  });

  controls.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
    if (isCurrentCustomer(participant)) {
      clearCustomerAudio();
      controls.callPhase = "waiting-caller";
      showSnackbar("Customer has left the call.");
    }
  });

  controls.room.on(RoomEvent.TrackMuted, (publication, participant) => {
    if (publication?.kind === Track.Kind.Audio && isCurrentCustomer(participant)) {
      controls.callerMuted = true;
      controls.callerSpeaking = false;
    }
  });

  controls.room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
    if (publication?.kind === Track.Kind.Audio && isCurrentCustomer(participant)) {
      controls.callerMuted = false;
    }
  });

  controls.room.on(RoomEvent.TrackStreamStateChanged, (track, streamState, participant) => {
    if (track.kind !== Track.Kind.Audio || !isCurrentCustomer(participant)) return;
    controls.callerAudioStreamState = (streamState || "").toString().toLowerCase();
    controls.callPhase = controls.callerAudioStreamState === "paused" ?
      "waiting-caller" : "active";
  });

  controls.room.on(RoomEvent.TrackSubscriptionFailed, (_, participant) => {
    if (!isCurrentCustomer(participant)) return;
    clearCustomerAudioTrack();
    controls.callPhase = "waiting-caller";
    controls.callError = "Caller audio could not be subscribed. Waiting to retry.";
    reportHealthEvent("audio_blocked", "agent");
  });

  controls.room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
    controls.callerSpeaking = speakers.some(
        (participant) => participant.identity === controls.uidOfCustomerOnCall,
    );
    controls.agentSpeaking = speakers.some(
        (participant) => participant.isLocal,
    );
  });

  controls.room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
    controls.audioBlocked = !controls.room.canPlaybackAudio;
    if (controls.audioBlocked) {
      reportHealthEvent("audio_blocked", "agent");
    }
  });

  controls.room.on(RoomEvent.MediaDevicesError, () => {
    controls.microphonePermission = "denied";
    controls.micOn = false;
  });

  controls.room.on(RoomEvent.MediaDevicesChanged, syncPlaybackDevices);
};

const performAnswerCall = async (callerId, setTime, onGoingCallBeforeRelaoding) => {
  if (controls.endCallPromise) return {ok: false, reason: "call-ending"};
  const callRoomId = controls.roomId;
  const attempt = ++controls.callAttempt;
  const cancelled = () => attempt !== controls.callAttempt;
  let callRoom = null;
  let assignedByBackend = Boolean(onGoingCallBeforeRelaoding);
  try {
    if (controls.agentActivityState === "break") {
      showSnackbar("Resume availability before answering a call.", "warning");
      return {ok: false, reason: "on-break"};
    }
    controls.callPhase = "assigning";
    controls.callError = "";
    controls.callAnswered = true;
    controls.agentActivityState = "on_call";
    controls.callerIdOnCall = callerId;
    controls.intentionalDisconnect = false;

    if (controls.microphonePermission !== "ready" &&
        !await prepareAgentMicrophone()) {
      controls.callAnswered = false;
      controls.callerIdOnCall = "";
      controls.callPhase = "idle";
      controls.agentActivityState = "available";
      showSnackbar(
          "Allow a working microphone before answering the call.",
          "error",
      );
      return {ok: false, reason: "microphone-unavailable"};
    }

    const functions = getFunctions();
    const answerAUserCall = httpsCallable(functions, "answerAUserCall");

    const result = await answerAUserCall({
        nameOfAgent: controls.channelName,
        callerId: callerId,
        roomId: controls.roomId,
        audioBitrateKbps: controls.callAudioBitrateKbps,
      });

    const answerStatus = Boolean(result.data?.[0]);
    if (cancelled()) {
      if (answerStatus) {
        await httpsCallable(functions, "handleUserCallQueue")({
          callerId, roomId: callRoomId, historyDocId: result.data?.[1],
        });
      }
      return {ok: false, reason: "call-ended"};
    }
    assignedByBackend = assignedByBackend || answerStatus;
    if (!assignedByBackend) {
      controls.callAnswered = false;
      controls.callerIdOnCall = "";
      controls.callPhase = "idle";
      controls.callError = "";
      const reason = result.data?.[2];
      showSnackbar(
          reason === "agent-on-break" ?
            "Resume availability before answering a call." :
            "This call was already answered by another agent.",
          "warning",
      );
      return {ok: false, reason: "already-answered"};
    }

    controls.historyDocId = result.data?.[1] || controls.historyDocId;
    controls.mediaRoomName = result.data?.[3] || controls.channelName;
    // Request the token after assignment because guarded recording modes use a
    // unique media room that is created atomically for this call.
    const tokenReady = await setLiveKitToken();
    if (cancelled()) return {ok: false, reason: "call-ended"};
    if (!tokenReady || (controls.token || "").length < 2) {
      throw new Error("A fresh LiveKit token could not be created.");
    }
    controls.callPhase = "connecting";
    clearCustomerAudio();
    controls.uidOfCustomerOnCall = `caller-${callerId}`;
    controls.callerMuted = false;
    controls.agentSpeaking = false;
    initLiveKitRoom();
    callRoom = controls.room;
    await callRoom.connect(controls.livekitUrl, controls.token);
    if (cancelled()) {
      await callRoom.disconnect();
      return {ok: false, reason: "call-ended"};
    }
    await callRoom.startAudio?.();
    if (cancelled()) return {ok: false, reason: "call-ended"};
    // Calls always begin unmuted. Muting is scoped to the current call and
    // must never leak into the next customer session.
    controls.micOn = true;
    if (controls.microphonePermission !== "ready") {
      await prepareAgentMicrophone();
    }
    if (cancelled()) return {ok: false, reason: "call-ended"};
    const microphonePublished = await publishAgentMicrophone();
    if (cancelled()) {
      await callRoom.disconnect();
      return {ok: false, reason: "call-ended"};
    }
    // Keep the publication present so Auto Track Egress starts immediately,
    // but prevent speech from leaving the browser until recording is confirmed.
    controls.microphoneTrack?.mute?.();
    controls.agentConnectionQuality = controls.room.localParticipant.connectionQuality;
    await syncPlaybackDevices();
    if (controls.preferredOutputDeviceId) {
      const preferred = controls.playbackDevices.find(
          (device) => device.deviceId === controls.preferredOutputDeviceId,
      );
      if (preferred) await switchAgentOutput(preferred);
    }
    if (cancelled()) return {ok: false, reason: "call-ended"};
    await controls.requestWakeLock();
    if (cancelled()) {
      await controls.releaseWakeLock();
      return {ok: false, reason: "call-ended"};
    }
    showSnackbar(
        microphonePublished ?
          "Call assigned. Waiting for customer audio." :
          "Call connected, but your microphone is not sending audio. Choose a microphone in Audio.",
        microphonePublished ? "success" : "warning",
    );
    document.getElementById("myPlayer")?.pause?.();

    controls.callAnswered = true;
    controls.callPhase = controls.audioTrackOfCustomer ? "active" : "waiting-caller";
    controls.callDuration = onGoingCallBeforeRelaoding ? controls.callDuration : 0;
    setTime(controls.callDuration);
    controls.timeAnswered = 0;
    controls.recordingStatus = "waiting";
    controls.recordingError = "";
    controls.recordingStartPromise = null;
    controls.lastHealthEvents = {};
    clearInterval(controls.myInterval);
    controls.myInterval = null;

    const existingCaller = controls.room.remoteParticipants
        ?.get(controls.uidOfCustomerOnCall);
    if (existingCaller?.isActive) {
      controls.callerConnected = true;
      controls.callerActive = true;
      controls.callerParticipant = existingCaller;
      controls.callerMuted = !existingCaller.isMicrophoneEnabled;
      if (controls.audioTrackOfCustomer) {
        await startRecordingForCurrentCall();
      }
    }

    return {ok: true};
  } catch (err) {
    if (cancelled()) {
      try { await callRoom?.disconnect?.(); } catch (_) {}
      return {ok: false, reason: "call-ended"};
    }
    controls.callPhase = assignedByBackend ? "failed" : "idle";
    controls.callError = assignedByBackend ?
      "Could not connect to LiveKit. Check your network and retry." :
      "Could not answer this call. Please try again.";
    controls.callAnswered = assignedByBackend;
    if (!assignedByBackend) controls.callerIdOnCall = "";
    controls.intentionalDisconnect = true;
    try {
      await controls.room?.disconnect?.();
    } catch (_) {}
    controls.intentionalDisconnect = false;
    await controls.releaseWakeLock();
    showSnackbar(controls.callError, "error");
    console.log(err);
    return {ok: false, reason: assignedByBackend ? "connection-failed" : "answer-failed"};
  }
};

const answerCall = (callerId, setTime, ongoing) => {
  if (controls.answerCallPromise || controls.endCallPromise) {
    return Promise.resolve({ok: false, reason: "call-transition-in-progress"});
  }
  controls.answerCallPromise = performAnswerCall(callerId, setTime, ongoing)
    .finally(() => { controls.answerCallPromise = null; });
  return controls.answerCallPromise;
};

const queueBack = async (callerId, newRoomId, newRoomName, setTime, setMicOn) => {
  const functions = getFunctions();
  const handleUserCallQueue = httpsCallable(functions, "handleUserCallQueue");

  try {
    controls.setLoading([true, ""]);

    await handleUserCallQueue({
      queueBack: true,
      callerId: callerId,
      roomId: controls.roomId,
      newRoomId: newRoomId,
      newRoomName: newRoomName,
    }).then(async (result) => {
      const status = result.data;

      showSnackbar(`Customer has been sent to ${newRoomName} room.`);

      if (status) {
        endCall(setMicOn, setTime, callerId);
      }
    });
    controls.setLoading([false, ""]);
  } catch (_) {
    controls.setLoading([false, ""]);
  }
};

const performEndCall = async (setMicOn, setTime, id, reason) => {
  const functions = getFunctions();
  const handleUserCallQueue = httpsCallable(functions, "handleUserCallQueue");
  const stopRecording = httpsCallable(functions, "stopRecording");
  const room = controls.room;
  const call = {
    callerId: id,
    email: "",
    roomId: controls.roomId,
    historyDocId: controls.historyDocId,
  };
  const recording = {
    roomId: call.roomId,
    historyDocId: call.historyDocId,
    egressId: controls.egressId,
    roomName: controls.mediaRoomName || controls.channelName,
  };
  controls.callPhase = "ending";
  controls.intentionalDisconnect = true;
  controls.callAttempt += 1;
  controls.callAnswered = false;
  controls.micOn = false;
  controls.recordingStatus = "ending";

  // Stop local audio and request disconnect before either network request.
  const media = disconnectCallMedia(room, controls.microphoneTrack, reason);
  clearCustomerAudio();
  clearInterval(controls.myInterval);
  controls.myInterval = null;
  controls.callDuration = 0;
  controls.timeAnswered = 0;
  setTime(0);
  setMicOn(false);
  const endRequest = handleUserCallQueue({...call, endTiming: media.timing});
  const stopRequest = stopRecording(recording);
  const requests = Promise.allSettled([endRequest, stopRequest]);
  media.done.then((endTiming) =>
    handleUserCallQueue({...call, endTimingOnly: true, endTiming})
  ).catch(() => {});
  try {
    const [endResult, stopResult] = await requests;
    if (endResult.status === "rejected" || !endResult.value?.data) {
      showSnackbar("Call timing could not be finalized. It will be retried.", "warning");
    }
    if (stopResult.status === "fulfilled" && stopResult.value?.data?.ok) {
      controls.recordingStatus = "ended";
    } else {
      // The durable server job also stops recording and retries independently.
      controls.recordingStatus = "ending";
    }
    showSnackbar("Call ended");
    controls.callerIdOnCall = "";
    controls.mediaRoomName = "";
    controls.agentActivityState = "available";
    controls.callPhase = "idle";
    controls.callError = "";
    controls.roomConnectionState = room?.state || "disconnected";
    controls.recordingStartPromise = null;
    controls.egressId = "";
    controls.microphoneTrack = null;
  } finally {
    await controls.releaseWakeLock();
  }
};

const endCall = (setMicOn, setTime, id, reason = "hangup") => {
  if (controls.endCallPromise) return controls.endCallPromise;
  controls.endCallPromise = performEndCall(setMicOn, setTime, id, reason)
      .finally(() => {
        controls.endCallPromise = null;
      });
  return controls.endCallPromise;
};

const setOnAuthStateChanged = () =>
  onAuthStateChanged(auth, (user) => {
    if (user) {
      controls.agentUserId = user.uid;
      controls.setView("call");
      controls.setLoading([false, ""]);
    } else {
      controls.agentUserId = null;
      controls.setView("signin");
      controls.setLoading([false, ""]);
      controls.releaseWakeLock();
    }
  });

const signIn = (email, password) => {
  controls.setLoading([true, "Signing you in..."]);

  signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        showSnackbar("Sign in succeesful!");
      })
      .catch(() => {});
};

const resetPassword = async (email) => {
  controls.setLoading([true, ""]);

  if (email.length < 4 || !email.includes("@")) {
    showSnackbar("Enter a valid email first!", "error");
    return;
  }

  await sendPasswordResetEmail(auth, email).then(() => {
    showSnackbar(`Password reset email sent to your email:${email}`);
    controls.setLoading([false, ""]);
  })
      .catch(() => {
        controls.setLoading([false, ""]);
      });
};

const signUp = (email, password) => {
  controls.setLoading([true, "Signing up..."]);

  createUserWithEmailAndPassword(auth, email, password)
      .then(() => {
        showSnackbar("Sign up succeesful!");
      })
      .catch(() => {});
};

export default controls;

export {
  answerCall,
  acquireAgentMicrophoneStream,
  endCall,
  initLiveKitRoom,
  prepareAgentMicrophone,
  publishAgentMicrophone,
  setAgentCallAudioBitrateKbps,
  unlockAgentAudioPlayback,
  queueBack,
  resetPassword,
  resumeAgentAudio,
  setOnAuthStateChanged,
  signIn,
  signUp,
  switchAgentOutput,
  switchAgentInput,
  syncPlaybackDevices,
  runAgentConnectionDiagnostic,
  setAgentCallVolume,
};
